<template>
  <div>
    <section class="hero is-medium is-primary is-bold">
      <div class="hero-body">
        <div class="container">
          <h1 v-if="event.title" class="title">{{event.title}}</h1>
          <h1 v-else class="title">Loading...</h1>
          <h2 v-if="event.tagline" class="subtitle">{{event.tagline}}</h2>
          <h2 v-else class="subtitle">&nbsp;</h2>
        </div>
      </div>
      <div class="hero-footer">
        <div class="container">
          <nav class="level">
            <div class="level-left">
            </div>
            <div class="level-right">
              <div v-if="isEventOwner && event.id && !isEditing" class="level-item">
                <router-link :to="`/event/${event.id}/edit`" class="button is-link">
                  <span class="icon">
                    <i class="fas fa-edit"></i>
                  </span>
                  <span>Edit Event</span>
                </router-link>
              </div>
              <div v-else-if="isEventOwner && event.id && isEditing" class="level-item">
                <button class="button is-danger" :disabled="isSaving"
                    v-on:click="revertChanges">
                  <span class="icon">
                    <i class="fas fa-times"></i>
                  </span>
                  <span>Revert Changes</span>
                </button>
                <button class="button is-primary" :class="isSaving ? 'is-loading' : ''"
                    v-on:click="commitChanges">
                  <span class="icon">
                    <i class="fas fa-save"></i>
                  </span>
                  <span>Save Changes</span>
                </button>
              </div>
            </div>
          </nav>
        </div>
      </div>
    </section>
    <section class="section">
      <div class="columns">
        <div class="column is-one-quarter">
          <h4 class="has-text-weight-bold is-uppercase is-size-6">
            <span class="icon is-small is-left">
                <i class="fas fa-clock"></i>
              </span>
              Date &amp; Time
          </h4>
          <p>
            <ul>
              <li>
                <EditableDateTime v-model="event.startDate" :isEditting="isEditing" :isSaving="isSaving">
                  Start: {{new Date(event.startDate).toLocaleString()}}
                </EditableDateTime>
              </li>
              <li>
                <EditableDateTime v-model="event.endDate" :isEditting="isEditing" :isSaving="isSaving">
                  End: {{new Date(event.endDate).toLocaleString()}}
                </EditableDateTime>
              </li>
            </ul>
          </p>
          <h4 class="has-text-weight-bold is-uppercase is-size-6">
            <span class="icon is-small is-left">
                <i class="fas fa-globe"></i>
              </span>
              Location
          </h4>
          <EditableInput v-model="event.publicAddressName" :isEditting="isEditing" :isSaving="isSaving">
            {{event.publicAddressName}}
            <span class="icon is-small has-text-grey" title="This public. Anyone viewing this event can see it.">
              <i class="fas fa-lock-open"></i>
            </span>
          </EditableInput>
          <EditableInput v-model="event.publicAddress" :isEditting="isEditing" :isSaving="isSaving">
            {{event.publicAddress}}
            <span class="icon is-small has-text-grey" title="This public. Anyone viewing this event can see it.">
              <i class="fas fa-lock-open"></i>
            </span>
          </EditableInput>
          <EditableInput v-model="locationString" :isEditting="isEditing" :isSaving="true">
            {{locationString}}
            <a v-on:click="copyLocation" title="Copy to clipboard">
              <i class="fas fa-clipboard"></i>
            </a>
          </EditableInput>
          <h4 class="has-text-weight-bold is-uppercase is-size-6">
            <span class="icon is-small is-left">
              <i class="fas fa-money-bill"></i>
            </span>
            Cost
          </h4>
          <EditableInput v-model="event.cost" :isEditting="isEditing" :isSaving="isSaving"></EditableInput>
          <h4 class="has-text-weight-bold is-uppercase is-size-6">
            <span class="icon is-small is-left">
              <i class="fas fa-user"></i>
            </span>
            Organizer
          </h4>
          <p v-if="event.organizer">
            <router-link :to="`/profile/${event.organizer.id}/Username`">Username</router-link>
          </p>
          <p v-else>
            <span class="icon">
              <i class="fas fa-spinner fa-pulse"></i>
            </span>
          </p>
          <h4 class="has-text-weight-bold is-uppercase is-size-6">
            <span class="icon is-small is-left">
              <i class="fas fa-envelope-open"></i>
            </span>
            RSVP
          </h4>
          <nav class="level is-mobile">
            <div class="level-item has-text-centered">
              <div>
                <p class="heading">Yes</p>
                <p class="title" v-if="rsvp.rsvps && rsvp.rsvps.attending">
                  {{Number(rsvp.rsvps.attending.length).toLocaleString()}}
                </p>
                <p class="title" v-else>
                  <span class="icon">
                    <i class="fas fa-spinner fa-pulse"></i>
                  </span>
                </p>
              </div>
            </div>
            <div class="level-item has-text-centered">
              <div>
                <p class="heading">Maybe</p>
                <p class="title" v-if="rsvp.rsvps && rsvp.rsvps.maybe_attending">
                  {{Number(rsvp.rsvps.maybe_attending.length).toLocaleString()}}
                </p>
                <p class="title" v-else>
                  <span class="icon">
                    <i class="fas fa-spinner fa-pulse"></i>
                  </span>
                </p>
              </div>
            </div>
            <div class="level-item has-text-centered">
              <div>
                <p class="heading">No</p>
                <p class="title" v-if="rsvp.rsvps && rsvp.rsvps.not_attending">
                  {{Number(rsvp.rsvps.not_attending.length).toLocaleString()}}
                </p>
                <p class="title" v-else>
                  <span class="icon">
                    <i class="fas fa-spinner fa-pulse"></i>
                  </span>
                </p>
              </div>
            </div>
          </nav>
          <div class="buttons has-addons is-centered">
            <button class="button is-success"
                :class="[
                  selfRsvp.rsvp && selfRsvp.rsvp.response === 'ATTENDING' ? '' : 'is-light',
                  isSavingRsvp ? 'is-loading' : '',
                ]"
                v-on:click="updateRsvp('ATTENDING')">Yes</button>
            <button class="button is-info"
                :class="[
                  selfRsvp.rsvp && selfRsvp.rsvp.response === 'MAYBE_ATTENDING' ? '' : 'is-light',
                  isSavingRsvp ? 'is-loading' : '',
                ]"
                v-on:click="updateRsvp('MAYBE_ATTENDING')">Maybe</button>
            <button class="button is-danger"
                :class="[
                  selfRsvp.rsvp && selfRsvp.rsvp.response === 'NOT_ATTENDING' ? '' : 'is-light',
                  isSavingRsvp ? 'is-loading' : '',
                ]"
                v-on:click="updateRsvp('NOT_ATTENDING')">No</button>
          </div>
        </div>
        <div class="column">
          <div v-if="eventInThePast" class="columns">
            <div class="column"></div>
            <div class="column is-half">
              <article class="message is-warning">
                <div class="message-body">
                  <strong>Oh no!</strong> Looks like this event has already happened!
                </div>
              </article>
            </div>
            <div class="column"></div>
          </div>
          <div v-if="eventHappeningNow" class="columns">
            <div class="column"></div>
            <div class="column is-half">
              <article class="message is-success">
                <div class="message-body">
                  <strong>Hurray!</strong> This event is happening right now!
                  <span class="icon">
                    <i class="fas glass-cheers"></i>
                  </span>
                </div>
              </article>
            </div>
            <div class="column"></div>
          </div>
          <div class="container">
            <EditableRichText v-model="event.description" :isEditting="isEditing" :isSaving="isSaving">
              {{event.description}}
              <h3 class="title is-4">Debug Info</h3>  
              <pre>{{event}}</pre>
            </EditableRichText>
          </div>
        </div>
      </div>
      <div class="tabs is-centered is-boxed">
        <ul>
          <li class="is-active">
            <a>
              <span class="icon is-small"><i class="fas fa-comments" aria-hidden="true"></i></span>
              <span>Forums</span>
            </a>
          </li>
          <li>
            <a>
              <span class="icon is-small"><i class="fas fa-poll" aria-hidden="true"></i></span>
              <span>Survey</span>
            </a>
          </li>
        </ul>
      </div>
      <article class="message">
        <div class="message-body">
          Coming soon!  
        </div>
      </article>
    </section>
  </div>
</template>
<script>
import api from '@/api';
import EditableDateTime from '@/components/forms/EditableDateTime';
import EditableInput from '@/components/forms/EditableInput';
import EditableRichText from '@/components/forms/EditableRichText';

export default {
  name: 'GetEvent',
  props: ['userInfo'],
  components: {
    EditableDateTime,
    EditableInput,
    EditableRichText,
  },
  data() {
    return {
      event: {},
      eventOriginal: {},
      rsvp: {},
      isSaving: false,
      isSavingRsvp: false,
    };
  },
  mounted() {
    this.fetchEventData(this.$route.params.eventId);
  },
  beforeRouteUpdate(to, from, next) {
    if (event.id !== to.params.eventId) {
      this.fetchEventData(to.params.eventId);
    }
    next();
  },
  methods: {
    async fetchEventData(eventId) {
      this.event = {};
      this.eventOriginal = {};
      this.rsvp = {};

      if (await api.guard()) {
        api.get(`/event/${eventId}`).then((response) => {
          this.eventOriginal = response;
          // Deep clone it (not memory efficient, but will get the job done for now)
          this.event = JSON.parse(JSON.stringify(this.eventOriginal));
        }).catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        });
        api.get(`/event/${eventId}/rsvp`).then((response) => {
          this.rsvp = response;
        }).catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        });
      }
    },
    copyLocation() {
      navigator.clipboard.writeText(this.locationString).then(function() {
        /* clipboard successfully set */
      }, function() {
        /* clipboard write failed */
      });
    },
    revertChanges() {
      // Deep clone it (not memory efficient, but will get the job done for now)
      this.event = JSON.parse(JSON.stringify(this.eventOriginal));
      this.$router.push(`/event/${this.event.id}`);
    },
    async commitChanges() {
      this.isSaving = true;

      if (await api.guard()) {
        api.put(`/event/${this.eventId}`, {
          body: this.event
        }).then((response) => {
          this.eventOriginal = response;
          // Deep clone it (not memory efficient, but will get the job done for now)
          this.event = JSON.parse(JSON.stringify(this.eventOriginal));

          this.isSaving = false;
          this.isEditing = false;

          this.$router.push({
            path: `/event/${response.id}`,
          });
        }).catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
          this.isSaving = false;
        });
      }
    },
    async updateRsvp(status) {
      this.isSavingRsvp = true;

      // If updating to the same status, then delete it instead
      if (this.selfRsvp.rsvp && this.selfRsvp.rsvp.response === status) {
        if (await api.guard()) {
          api.delete(`/event/${this.eventId}/rsvp`).then(() => {
            if (this.selfRsvp.rsvp) {
              // Remove the old RSVP
              const rsvpType = this.selfRsvp.rsvp.response.toLowerCase();
              const i = this.rsvp.rsvps[rsvpType].indexOf(this.selfRsvp);
              if (i !== -1) {
                this.rsvp.rsvps[rsvpType].splice(i, 1);
              }
            }

            this.isSavingRsvp = false;
          }).catch((err) => {
            // eslint-disable-next-line no-console
            console.error(err);
            this.isSavingRsvp = false;
          });
        }
        return;
      }

      if (await api.guard()) {
        api.put(`/event/${this.eventId}/rsvp`, {
          body: status
        }).then((response) => {
          for (const rsvpType in this.rsvp.rsvps) {
            for (const [i, rsvp] of this.rsvp.rsvps[rsvpType].entries()) {
              if (rsvp.user.id === this.userInfo.attributes.sub) {
                // Remove the old RSVP that we found
                this.rsvp.rsvps[rsvpType].splice(i, 1);
                break;
              }
            }
          }

          // Add the new RSVP into the object to correctly update the UI
          const rsvpType = Object.keys(response.rsvps)[0];
          this.rsvp.rsvps[rsvpType].push(response.rsvps[rsvpType][0]);

          this.isSavingRsvp = false;
        }).catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
          this.isSavingRsvp = false;
        });
      }
    },
  },
  computed: {
    eventId() {
      if (!this.event.id) {
        return this.$route.params.eventId;
      }
      return this.event.id;
    },
    selfRsvp() {
      if (!this.rsvp || !this.rsvp.rsvps) {
        return {};
      }
      for (const rsvpType in this.rsvp.rsvps) {
        for (const rsvp of this.rsvp.rsvps[rsvpType]) {
          if (rsvp.user.id === this.userInfo.attributes.sub) {
            return rsvp;
          }
        }
      }
      return {};
    },
    locationString() {
      const location = this.event.location;
      if (!location) {
        return undefined;
      }
      return `${location.city}, ${location.state}, ${location.country}`;
    },
    eventInThePast() {
      if (!this.event.endDate) {
        return false;
      }

      const now = new Date();
      const endDate = new Date(this.event.endDate);
      return endDate < now;
    },
    eventHappeningNow() {
      if (!this.event.startDate || !this.event.endDate) {
        return false;
      }
      const now = new Date();
      const startDate = new Date(this.event.startDate);
      const endDate = new Date(this.event.endDate);
      return startDate < now && now < endDate;
    },
    isEventOwner() {
      if (!this.event.organizer || !this.event.organizer.id) {
        return false;
      }
      return this.event.organizer.id === this.userInfo.attributes.sub;
    },
    isEditing() {
      return this.$route.path.endsWith('/edit');
    }
  },
}
</script>